import { CandidateType } from "building-types/electrical/candidateTypesEnum";
import type { ControllerSelectionConfig } from "building-types/electrical/controllerSelectionTypes";
import type { LinearArray } from "building-types/electrical/electricalLayoutTypes";
import type { ElectricalLayoutType } from "building-types/electrical/electricalLayoutTypesEnum";
import { RoomBoundaryType, SpacingType } from "building-types/electrical/electricalLayoutTypesEnum";
import { ControllerSwitchType, ControllerType } from "building-types/electrical/electricalTypesEnum";
import type {
  ControllerPosition,
  GeneratedElectricalGroup,
  Load,
} from "building-types/electrical/generatedElectricalTypes";
import type { Position } from "building-types/rooms/roomPosition";
import { assertIsNotNullOrUndefined, assertIsNotUndefined, assertIsNumber } from "common/utils/assertOfType";
import { getLoadCatalog } from "doc/getLoadCatalog";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { classifyContainmentVec2PolyLine2, Vec2PolyLine2ContainmentClass } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import type { Ray2PolyLine2Intersection } from "geom/intersectRay2";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { poleOfInaccessibilityPolygon } from "geom/label";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2MergeCollinearEdges,
} from "geom/polyLine2";
import { polyLine2MaximalRectangle } from "geom/polyLine2MaximalRect";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rangeInclusiveByStep, rangeNonInclusiveByCount } from "geom/range";
import { vec2Dist } from "geom/vec2";
import { commitControllerSelection } from "../controllerSelection/controllerSelectionCommit";
import { candidateFilteringCriteriaNew } from "../controllerSelection/controllerSelectionShared";
import type { CandidateFilteringCriteria, ControllerSelectionRoomIdFunc } from "../electricalLayoutTypes";
import type { ElectricalLayoutFunc, ElectricalLayoutOpts } from "../electricalLayoutTypes";

export function linearArray({
  layoutType: type,
  controllerPlacementType,
  endControllerPlacementType,
  loadType,
  spacing,
  spacingType,
  shift,
  quantity,
  trimToRoom,
  minDistToWall,
  roomBoundaryType,
}: LinearArray): ElectricalLayoutFunc {
  const resolvedRoomBoundaryType = roomBoundaryType === undefined ? RoomBoundaryType.BOUNDING_BOX : roomBoundaryType;

  return async (opts: ElectricalLayoutOpts): Promise<GeneratedElectricalGroup[]> => {
    const { room, roomOutlines, controllerSelectionFunc, electricalItemCatalogSettings } = opts;
    const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
    const loadDescriptor = loadCatalog[loadType];
    const insideFramingOutline = roomOutlines.INSIDE_OF_MASS_IGNORE_VOID;
    assertIsNotNullOrUndefined(insideFramingOutline);
    let outline = polyLine2MergeCollinearEdges(polyLine2Copy(insideFramingOutline));
    if (resolvedRoomBoundaryType === RoomBoundaryType.MAX_RECTANGLE) {
      const { maxRectAsPolyLine2 } = polyLine2MaximalRectangle(outline);
      outline = maxRectAsPolyLine2;
    }
    const maxLengthIndex = getMaxLengthEdgeIndex(outline);
    if (maxLengthIndex === -1) {
      return [];
    }
    assertIsNotUndefined(maxLengthIndex);
    const [originalOutlineX, originalOutlineY] = polyLine2EdgeEval(outline, maxLengthIndex, 0.5, true);
    const [nx, ny] = polyLine2EdgeNormal(outline, maxLengthIndex, true);
    let [tx, ty] = polyLine2EdgeTangent(outline, maxLengthIndex, false);
    // When a non-zero shift is specified, ensure the tangent direction is consistent
    // with the shift so that the shift is applied consistently regardless of outline vertex winding.
    // Without this, mirrored rooms can reverse the tangent, causing the shift to be applied
    // in the opposite direction. We only ensure consistency when shift is present to avoid
    // changing load ordering (and thus wiring topology) for zero-shift layouts.
    if (shift && (ty > 0 || (Math.abs(ty) < DIST_EPSILON && tx < 0))) {
      tx = -tx;
      ty = -ty;
    }
    const { sourceX, sourceY } = getArraySourcePoint(originalOutlineX, originalOutlineY, nx, ny, outline);

    const ints =
      sourceX !== undefined && sourceY !== undefined ? intersectRay2PolyLine2(outline, sourceX, sourceY, tx, ty) : [];
    ray2PolyLine2IntersectionMakeUnique(ints);
    const positiveInts: Ray2PolyLine2Intersection[] = [];
    const negativeInts: Ray2PolyLine2Intersection[] = [];
    ints.forEach((x) => {
      if (x.t < 0) {
        negativeInts.push(x);
      } else {
        positiveInts.push(x);
      }
    });

    if (positiveInts.length === 0 || negativeInts.length === 0) {
      return [];
    }

    const { x: sx, y: sy } = negativeInts[negativeInts.length - 1];
    const { x: ex, y: ey } = positiveInts[0];

    const resolvedShift = shift || 0;
    const resolvedMinDistToWall = shift === undefined ? minDistToWall || 0 : 0;
    const resolvedSpacingType = spacingType === undefined ? SpacingType.SEGMENT_END_POINTS : spacingType;

    const paramInfos = getParams(
      quantity,
      spacing,
      resolvedSpacingType,
      sx,
      sy,
      ex,
      ey,
      resolvedShift,
      resolvedMinDistToWall,
    );

    const positions = getPositionsFromParams(paramInfos, sx, sy, ex, ey, nx, ny);

    const loads: Load[] = [];
    for (const pos of positions) {
      const {
        location: [x, y],
        normal: [nmx, nmy],
      } = pos;

      if (trimToRoom) {
        if (classifyContainmentVec2PolyLine2(x, y, outline) !== Vec2PolyLine2ContainmentClass.INSIDE) {
          continue;
        }
      }

      loads.push({
        loadType,
        loadDescriptor,
        position: [x, y],
        normal: [nmx, nmy],
        levelId: room.level.id,
        sourceRoomHandle: room.roomHandle,
      });
    }

    if (loads.length === 0) {
      return [];
    }

    const candidateFilteringCriteria = candidateFilteringCriteriaNew();
    const { position: startControllerPosition, candidateId: startCandidateId } = await makeControllerSelection(
      controllerPlacementType,
      loads,
      0,
      controllerSelectionFunc,
      type,
      candidateFilteringCriteria,
    );
    const { position: endControllerPosition, candidateId: endCandidateId } = await makeControllerSelection(
      endControllerPlacementType,
      loads,
      loads.length - 1,
      controllerSelectionFunc,
      type,
      candidateFilteringCriteria,
    );

    if (startControllerPosition && endControllerPosition) {
      return [
        {
          levelId: opts.room.level.id,
          loads,
          controllers: [
            {
              type: ControllerType.SWITCH,
              switchType: ControllerSwitchType.THREE_WAY,
              position: startControllerPosition,
              loadIndex: 0,
              levelId: room.level.id,
              sourceRoomHandle: room.roomHandle,
              candidateId: startCandidateId,
              labels: makeBuiltInLabelTable("addToElectricalPlan"),
            },
            {
              type: ControllerType.SWITCH,
              switchType: ControllerSwitchType.THREE_WAY,
              position: endControllerPosition,
              loadIndex: loads.length - 1,
              levelId: room.level.id,
              sourceRoomHandle: room.roomHandle,
              candidateId: endCandidateId,
              labels: makeBuiltInLabelTable("addToElectricalPlan"),
            },
          ],
        },
      ];
    }

    if (!startControllerPosition && !endControllerPosition) {
      return [
        {
          levelId: opts.room.level.id,
          loads,
          controllers: [],
        },
      ];
    }

    if (!endControllerPosition) {
      return [
        {
          levelId: opts.room.level.id,
          loads,
          controllers: [
            {
              type: ControllerType.SWITCH,
              switchType: ControllerSwitchType.STANDARD,
              position: startControllerPosition,
              loadIndex: 0,
              levelId: room.level.id,
              sourceRoomHandle: room.roomHandle,
              candidateId: startCandidateId,
              labels: makeBuiltInLabelTable("addToElectricalPlan"),
            },
          ],
        },
      ];
    }

    return [
      {
        levelId: opts.room.level.id,
        loads,
        controllers: [
          {
            type: ControllerType.SWITCH,
            switchType: ControllerSwitchType.STANDARD,
            position: endControllerPosition,
            loadIndex: loads.length - 1,
            levelId: room.level.id,
            sourceRoomHandle: room.roomHandle,
            candidateId: undefined,
            labels: makeBuiltInLabelTable("addToElectricalPlan"),
          },
        ],
      },
    ];
  };
}

function getMaxLengthEdgeIndex(outline: PolyLine2) {
  let maxLength = -1;
  let maxLengthIndex: number | undefined;
  for (let i = 0, l = polyLine2EdgeCount(outline); i < l; i++) {
    const length = polyLine2EdgeLength(outline, i);
    if (length > maxLength) {
      maxLength = length;
      maxLengthIndex = i;
    }
  }
  return maxLengthIndex;
}

function getArraySourcePoint(
  originalOutlineX: number,
  originalOutlineY: number,
  nx: number,
  ny: number,
  outline: PolyLine2,
) {
  let sourceX: number | undefined;
  let sourceY: number | undefined;

  const insideOfFramingInts = intersectRay2PolyLine2(outline, originalOutlineX, originalOutlineY, nx, ny);
  ray2PolyLine2IntersectionMakeUnique(insideOfFramingInts);
  if (insideOfFramingInts.length < 1 || !insideOfFramingInts[0]) {
    const x = originalOutlineX;
    const y = originalOutlineY;
    const result = poleOfInaccessibilityPolygon([outline]);
    assertIsNotNullOrUndefined(result);
    const [xForWidth, yForWidth] = result;
    const ints = intersectRay2PolyLine2(outline, xForWidth, yForWidth, nx, ny);
    ray2PolyLine2IntersectionMakeUnique(ints);
    if (ints.length < 2) {
      if (ints.length === 1) {
        const { x: intx, y: inty } = ints[0];
        sourceX = intx;
        sourceY = inty;
      } else if (ints.length === 0) {
        sourceX = xForWidth;
        sourceY = yForWidth;
      }
    } else {
      const { x: intx, y: inty } = ints[1];
      [sourceX, sourceY] = polyLine2EdgeEval(polyLine2ByTwoPoints(x, y, intx, inty), 0, 0.5, true);
    }
  } else {
    const { x, y } = insideOfFramingInts[0];
    const ints = intersectRay2PolyLine2(outline, x, y, nx, ny);
    ray2PolyLine2IntersectionMakeUnique(ints);
    if (ints.length < 2) {
      if (ints.length === 1) {
        const { x: intx, y: inty } = ints[0];
        sourceX = intx;
        sourceY = inty;
      } else if (ints.length === 0) {
        sourceX = x;
        sourceY = y;
      }
    } else {
      const { x: intx, y: inty } = ints[1];
      [sourceX, sourceY] = polyLine2EdgeEval(polyLine2ByTwoPoints(x, y, intx, inty), 0, 0.5, true);
    }
  }
  return { sourceX, sourceY };
}

export function getPositionsFromParams(
  params: number[],
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  nx: number,
  ny: number,
) {
  const positions: Position[] = [];
  const edge = polyLine2ByTwoPoints(sx, sy, ex, ey);
  for (const param of params) {
    const [x, y] = polyLine2EdgeEval(edge, 0, param);
    positions.push({ location: [x, y], normal: [nx, ny] });
  }

  return positions;
}

export function getParams(
  quantity: number | undefined,
  spacing: number | undefined,
  spacingType: SpacingType,
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  resolvedShift: number,
  resolvedMinDistToWall: number,
) {
  const paramInfos: number[] = [];
  const dist = vec2Dist(sx, sy, ex, ey);

  if (quantity === 0) {
    // no matter what else is set, if quantity is 0, don't place anything
  } else if (quantity !== undefined && spacingIsUndefinedOrZero(spacing)) {
    let params: number[];
    switch (spacingType) {
      case SpacingType.SEGMENT_END_POINTS: {
        params = rangeNonInclusiveByCount(0, dist, quantity);
        break;
      }
      case SpacingType.SEGMENT_MID_POINT: {
        const gap = dist / quantity;
        const preParams: number[] = [];
        for (let i = 0; i < quantity; i++) {
          preParams.push(gap / 2 + gap * i);
        }
        params = preParams;
        break;
      }
    }

    for (const param of params) {
      paramInfos.push(param + resolvedShift);
    }
  } else if (quantity !== undefined && spacingIsDefinedAndGreaterThanMin(spacing, 0.1)) {
    assertIsNumber(spacing);
    const dist = vec2Dist(sx, sy, ex, ey);
    const distReq = (quantity - 1) * spacing;
    if (distReq < DIST_EPSILON) {
      paramInfos.push(dist / 2);
    } else {
      for (let i = 0; i < quantity; i++) {
        paramInfos.push(dist / 2 - distReq / 2 + (distReq / (quantity - 1)) * i + resolvedShift);
      }
    }
  } else if (quantityIsUndefinedOrLessThanOne(quantity) && spacingIsDefinedAndGreaterThanMin(spacing, 0.1)) {
    assertIsNumber(spacing);
    const dist = vec2Dist(sx, sy, ex, ey);
    const distAdjustedForMinDistToWall = dist - resolvedMinDistToWall * 2;

    if (distAdjustedForMinDistToWall < spacing) {
      paramInfos.push(dist / 2);
    } else {
      const distAdjusted = Math.floor(distAdjustedForMinDistToWall / spacing) * spacing;
      const startAdjust = (dist - distAdjusted) / 2;

      const params: number[] = rangeInclusiveByStep(0, distAdjusted, spacing);

      for (const param of params) {
        paramInfos.push(startAdjust + param + resolvedShift);
      }
    }
  }

  return paramInfos;
}

/**
 * TODO fix these checks to be more pedestrian and use type assertions
 */

function quantityIsUndefinedOrLessThanOne(quantity: number | undefined): boolean {
  return quantity === undefined || quantity < 1;
}

function spacingIsUndefinedOrZero(spacing: number | undefined): boolean {
  return spacing === undefined || spacing === 0;
}

function spacingIsDefinedAndGreaterThanMin(spacing: number | undefined, min: number) {
  return spacing !== undefined && spacing > min;
}

export async function makeControllerSelection(
  controllerPlacementType: ControllerSelectionConfig[] | undefined,
  loads: Load[],
  loadIndex: number,
  controllerSelectionFunc: ControllerSelectionRoomIdFunc,
  type: ElectricalLayoutType,
  candidateFilteringCriteria: CandidateFilteringCriteria,
): Promise<{ position: ControllerPosition | null; candidateId: string | null | undefined }> {
  if (!controllerPlacementType) {
    return { position: null, candidateId: null };
  }

  const load = loads[loadIndex];
  if (!load) {
    return { position: null, candidateId: null };
  }
  const {
    position: [cx, cy],
  } = load;
  const candidate = await controllerSelectionFunc(controllerPlacementType, [cx, cy], candidateFilteringCriteria);

  if (!candidate) {
    return { position: null, candidateId: null };
  }

  if (candidate.candidate.type === CandidateType.OPENING && candidate.candidate.sourceId) {
    candidateFilteringCriteria.doorIdsToIgnore.push(candidate.candidate.sourceId);
  }

  candidateFilteringCriteria.candidateIdsToIgnore.push(candidate.candidate.id);
  return {
    position: commitControllerSelection(candidate, type, ControllerType.SWITCH, [cx, cy]),
    candidateId: candidate?.candidate?.id,
  };
}
